import ShareIcon from "@mui/icons-material/Share";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { nodeToPngFile, shareOrDownloadPng } from "./partySharePng";
import type { PartyShareResultCardModel } from "./PartyShareResultCard";
import { PartyShareResultCard } from "./PartyShareResultCard";

async function waitForImages(root: HTMLElement, timeoutMs = 2500): Promise<void> {
  const imgs = Array.from(root.querySelectorAll("img"));
  if (imgs.length === 0) return;
  const pending = imgs.filter((im) => !(im.complete && im.naturalWidth > 0));
  if (pending.length === 0) return;

  await new Promise<void>((resolve) => {
    const t = window.setTimeout(resolve, timeoutMs);
    let left = pending.length;
    const done = () => {
      left -= 1;
      if (left <= 0) {
        window.clearTimeout(t);
        resolve();
      }
    };
    pending.forEach((im) => {
      im.addEventListener("load", done, { once: true });
      im.addEventListener("error", done, { once: true });
    });
  });
}

type Props = {
  model: Omit<PartyShareResultCardModel, "dir" | "lang">;
  className?: string;
  sx?: Record<string, unknown>;
};

export default function PartyShareResultButton({ model, className, sx }: Props) {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  const cardModel = useMemo((): PartyShareResultCardModel => {
    const rtl = Boolean(i18n.language?.startsWith("ar"));
    return {
      ...model,
      dir: rtl ? "rtl" : "ltr",
      lang: rtl ? "ar" : "en",
    };
  }, [i18n.language, model]);

  return (
    <>
      <Stack spacing={0.75} sx={{ width: "100%", alignItems: "center", justifyContent: "center", pt: 2 }}>
        <Button
          type="button"
          className={className}
          sx={sx}
          disabled={busy}
          startIcon={<ShareIcon />}
          onClick={async () => {
            if (!ref.current) return;
            try {
              setBusy(true);
              await waitForImages(ref.current);
              const file = await nodeToPngFile(ref.current, { fileNameBase: "kan-result" });
              await shareOrDownloadPng(file);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? t("common.loading") : t("common.share")}
        </Button>
        <Typography
          className="party-mono"
          sx={{ fontSize: 12, color: "var(--ink-dim)", textAlign: "center", lineHeight: 1.4, px: 1 }}
        >
          {t("party.share.finePrint")}
        </Typography>
      </Stack>

      {/* Off-screen render for PNG export */}
      <div
        style={{
          position: "fixed",
          left: -100000,
          top: 0,
          width: 1080,
          height: 1920,
          pointerEvents: "none",
          opacity: 0,
        }}
        aria-hidden
      >
        <PartyShareResultCard ref={ref} {...cardModel} />
      </div>
    </>
  );
}

